/**
 * @fileoverview Live Support Modal Component
 *
 * User-facing modal for live support screen sharing sessions.
 * Allows users to generate a support code and share their screen with admin support.
 *
 * Features:
 * - Generate 6-digit support code
 * - Copy code to clipboard
 * - Start/stop screen sharing
 * - Connection status indicators
 * - Session timer
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  FiMonitor,
  FiCopy,
  FiCheck,
  FiX,
  FiLoader,
  FiUser,
  FiUserCheck,
  FiVideo,
  FiVideoOff,
  FiClock,
  FiRefreshCw,
  FiAlertTriangle
} from 'react-icons/fi';
import Modal from './shared/Modal';
import ConfirmationModal from './shared/ConfirmationModal';
import { useLiveSupport } from '../context/LiveSupportContext';
import './LiveSupportModal.css';

// Check if screen sharing is supported (not available on mobile)
const isScreenShareSupported = () => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
};

/**
 * LiveSupportModal Component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function called when modal should close
 */
const LiveSupportModal = ({ isOpen, onClose }) => {
  const {
    session,
    status,
    error,
    generateSupportCode,
    startScreenShare,
    stopScreenShare,
    endSession,
    isConnected,
    isSharing,
  } = useLiveSupport();

  const [copied, setCopied] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const canShareScreen = isScreenShareSupported();

  // Session timer
  useEffect(() => {
    if (!session || status === 'idle') {
      setSessionDuration(0);
      return;
    }

    const startTime = new Date(session.created_at).getTime();
    const updateDuration = () => {
      const now = Date.now();
      setSessionDuration(Math.floor((now - startTime) / 1000));
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);

    return () => clearInterval(interval);
  }, [session, status]);

  // Format duration as mm:ss
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Copy code to clipboard
  const handleCopyCode = useCallback(async () => {
    if (!session?.code) return;

    try {
      await navigator.clipboard.writeText(session.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [session?.code]);

  // Handle generating new code
  const handleGenerateCode = useCallback(async () => {
    try {
      await generateSupportCode();
    } catch (err) {
      // Error handled in context
    }
  }, [generateSupportCode]);

  // Handle start sharing
  const handleStartSharing = useCallback(async () => {
    try {
      await startScreenShare();
    } catch (err) {
      // Error handled in context
    }
  }, [startScreenShare]);

  // Handle end session
  const handleEndSession = useCallback(async () => {
    await endSession();
    onClose?.();
  }, [endSession, onClose]);

  // Handle close
  const handleClose = useCallback(() => {
    if (status === 'sharing' || session) {
      // Show confirmation modal before closing
      setShowConfirmClose(true);
    } else {
      onClose?.();
    }
  }, [status, session, onClose]);

  // Handle confirmed close
  const handleConfirmClose = useCallback(async () => {
    setShowConfirmClose(false);
    await handleEndSession();
  }, [handleEndSession]);

  // Get confirmation message based on status
  const getConfirmMessage = () => {
    if (status === 'sharing') {
      return 'This will end your screen sharing session and disconnect the support agent.';
    }
    return 'This will cancel your support session. You will need to generate a new code to reconnect.';
  };

  // Get status display info
  const getStatusInfo = () => {
    switch (status) {
      case 'generating':
        return {
          icon: <FiLoader className="spin" />,
          text: 'Generating code...',
          color: 'var(--text-secondary)'
        };
      case 'waiting':
        return {
          icon: <FiUser />,
          text: 'Waiting for support agent...',
          color: 'var(--warning-color)'
        };
      case 'connecting':
        return {
          icon: <FiLoader className="spin" />,
          text: 'Connecting...',
          color: 'var(--text-secondary)'
        };
      case 'connected':
        return {
          icon: <FiUserCheck />,
          text: 'Support agent connected',
          color: 'var(--success-color)'
        };
      case 'sharing':
        return {
          icon: <FiVideo />,
          text: 'Screen sharing active',
          color: 'var(--success-color)'
        };
      case 'error':
        return {
          icon: <FiX />,
          text: error || 'An error occurred',
          color: 'var(--error-color)'
        };
      default:
        return {
          icon: <FiMonitor />,
          text: 'Ready to start',
          color: 'var(--text-secondary)'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      className="live-support-modal"
    >
      <div className="live-support-content">
        <div className="live-support-header">
          <FiMonitor className="header-icon" />
          <h2>Live Support</h2>
        </div>

        {/* Status indicator */}
        <div className="status-indicator" style={{ color: statusInfo.color }}>
          {statusInfo.icon}
          <span>{statusInfo.text}</span>
        </div>

        {/* No session - Show generate button */}
        {!session && status !== 'generating' && (
          <div className="no-session-content">
            <p className="description">
              Generate a support code to share your screen with our support team.
              They'll be able to see your screen to help troubleshoot issues.
            </p>
            <button
              className="btn btn-primary generate-btn"
              onClick={handleGenerateCode}
            >
              <FiMonitor />
              Generate Support Code
            </button>
          </div>
        )}

        {/* Has session - Show code and controls */}
        {session && (
          <>
            {/* Support code display */}
            <div className="code-display">
              <div className="code-label">Your Support Code</div>
              <div className="code-value">
                {session.code.split('').map((digit, i) => (
                  <span key={i} className="code-digit">{digit}</span>
                ))}
              </div>
              <button
                className="copy-btn"
                onClick={handleCopyCode}
                title="Copy to clipboard"
              >
                {copied ? <FiCheck /> : <FiCopy />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {/* Session timer */}
            <div className="session-timer">
              <FiClock />
              <span>{formatDuration(sessionDuration)}</span>
            </div>

            {/* Instructions */}
            {status === 'waiting' && (
              <p className="instructions">
                Share this code with the support agent. They will enter it to connect to your session.
              </p>
            )}

            {/* Screen share controls */}
            {isConnected && !isSharing && canShareScreen && (
              <div className="share-controls">
                <p className="instructions">
                  The support agent is connected. Click below to start sharing your screen.
                </p>
                <button
                  className="btn btn-primary share-btn"
                  onClick={handleStartSharing}
                >
                  <FiVideo />
                  Start Screen Share
                </button>
              </div>
            )}

            {isConnected && !isSharing && !canShareScreen && (
              <div className="share-controls">
                <div className="mobile-notice">
                  <FiVideoOff />
                  <p>Screen sharing is not available on mobile devices.</p>
                  <p className="mobile-notice-hint">Please use a desktop or laptop computer to share your screen.</p>
                </div>
              </div>
            )}

            {isSharing && (
              <div className="share-controls">
                <div className="sharing-indicator">
                  <div className="pulse-dot"></div>
                  <span>Your screen is being shared</span>
                </div>
                <button
                  className="btn btn-secondary stop-btn"
                  onClick={stopScreenShare}
                >
                  <FiVideoOff />
                  Stop Sharing
                </button>
              </div>
            )}

            {/* End session button */}
            <button
              className="btn btn-ghost end-session-btn"
              onClick={handleEndSession}
            >
              <FiX />
              End Session
            </button>
          </>
        )}

        {/* Error state - show retry */}
        {status === 'error' && !session && (
          <button
            className="btn btn-secondary retry-btn"
            onClick={handleGenerateCode}
          >
            <FiRefreshCw />
            Try Again
          </button>
        )}
      </div>

      {/* Confirmation modal for ending session */}
      <ConfirmationModal
        isOpen={showConfirmClose}
        onClose={() => setShowConfirmClose(false)}
        onConfirm={handleConfirmClose}
        title="End Support Session?"
        message={getConfirmMessage()}
        type="warning"
        confirmText="End Session"
        cancelText="Keep Session"
        icon={<FiAlertTriangle />}
      />
    </Modal>
  );
};

LiveSupportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default LiveSupportModal;
